"use strict";
/**
 * @since 2.0.0
 *
 * Represents a value of one of six possible types (a disjoint union).
 *
 * An instance of `DatumEither` is equivalent to `Datum<Either<E, A>>`
 *
 * A common use of `DatumEither` is as a container for dealing with refreshable data values that
 * can have error conditions. The full type list is:
 *
 * - `Initial`
 * - `Pending`
 * - `Refresh<Either<E, A>>`
 *   - `Refresh<Left<E>>`
 *   - `Refresh<Right<A>>`
 * - `Replete<Either<E, A>>`
 *   - `Replete<Left<E>>`
 *   - `Replete<Right<A>>`
 *
 * There are additional helper methods for going from refresh to replete and back.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
exports.__esModule = true;
var Either_1 = require("fp-ts/lib/Either");
var EitherT_1 = require("fp-ts/lib/EitherT");
var pipeable_1 = require("fp-ts/lib/pipeable");
var Datum_1 = require("./Datum");
exports.isValued = Datum_1.isValued;
exports.isInitial = Datum_1.isInitial;
exports.isPending = Datum_1.isPending;
exports.isRefresh = Datum_1.isRefresh;
exports.isReplete = Datum_1.isReplete;
var function_1 = require("fp-ts/lib/function");
/**
 * @since 2.0.0
 */
exports.URI = '@nll/datum/datum-either';
/**
 * @since 2.0.0
 */
exports.datumEither = __assign(__assign({}, EitherT_1.getEitherM(Datum_1.datum)), { URI: exports.URI });
/**
 * @since 2.1.0
 */
exports.success = function (a) { return Datum_1.replete(Either_1.right(a)); };
/**
 * @since 2.1.0
 */
exports.failure = function (e) { return Datum_1.replete(Either_1.left(e)); };
/**
 * @since 2.4.1
 */
exports.initial = Datum_1.initial;
/**
 * @since 2.4.1
 */
exports.pending = Datum_1.pending;
/**
 * @since 2.4.1
 */
exports.constInitial = function () { return exports.initial; };
/**
 * @since 2.4.1
 */
exports.constPending = function () { return exports.pending; };
/**
 * @since 2.7.0
 */
exports.isRefreshLeft = function (fea) { return Datum_1.isRefresh(fea) && Either_1.isLeft(fea.value); };
/**
 * @since 2.7.0
 */
exports.isRefreshRight = function (fea) { return Datum_1.isRefresh(fea) && Either_1.isRight(fea.value); };
/**
 * @since 2.7.0
 */
exports.isRepleteLeft = function (fea) { return Datum_1.isReplete(fea) && Either_1.isLeft(fea.value); };
/**
 * @since 2.7.0
 */
exports.isRepleteRight = function (fea) { return Datum_1.isReplete(fea) && Either_1.isRight(fea.value); };
/**
 * @since 2.1.0
 */
exports.isSuccess = function (fea) {
    return Datum_1.isValued(fea) && Either_1.isRight(fea.value);
};
/**
 * @since 2.1.0
 */
exports.isFailure = function (fea) {
    return Datum_1.isValued(fea) && Either_1.isLeft(fea.value);
};
/**
 * @since 2.1.0
 */
exports.toRefresh = function (fea) {
    return Datum_1.fold(exports.constPending, exports.constPending, function_1.constant(fea), Datum_1.refresh)(fea);
};
/**
 * @since 2.7.0
 */
exports.toReplete = function (fea) {
    return Datum_1.fold(exports.constInitial, exports.constInitial, Datum_1.replete, function_1.constant(fea))(fea);
};
/**
 * @since 2.2.0
 */
exports.fromEither = function (e) {
    return Datum_1.replete(e());
};
/**
 * @since 2.2.0
 */
exports.fromOption = function (onNone) { return function (o) { return Datum_1.replete(Either_1.fromOption(onNone)(o)); }; };
/**
 * Takes a nullable value, if the value is not nully, turn it into a `Success<A>`, otherwise `Initial`.
 *
 * @since 2.4.0
 */
exports.fromNullable = function (a) { return (a === null || a === undefined ? exports.initial : exports.success(a)); };
/**
 * @since 2.7.0
 */
exports.fold = function (onInitial, onPending, onRefreshLeft, onRefreshRight, onRepleteLeft, onRepleteRight) { return function (fea) {
    switch (fea._tag) {
        case 'Initial':
            return onInitial();
        case 'Pending':
            return onPending();
        case 'Refresh':
            return Either_1.fold(onRefreshLeft, onRefreshRight)(fea.value);
        case 'Replete':
            return Either_1.fold(onRepleteLeft, onRepleteRight)(fea.value);
    }
}; };
/**
 * @since 2.1.0
 */
exports.refreshFold = function (onInitial, onPending, onFailure, onSuccess) { return function (fea) {
    return Datum_1.fold(onInitial, onPending, function (e) { return (Either_1.isRight(e) ? onSuccess(e.right, true) : onFailure(e.left, true)); }, function (e) { return (Either_1.isRight(e) ? onSuccess(e.right, false) : onFailure(e.left, false)); })(fea);
}; };
/**
 * @since 2.3.0
 */
exports.squash = function (onNone, onFailure, onSuccess) { return function (fea) {
    return Datum_1.fold(function () { return onNone(false); }, function () { return onNone(true); }, function (e) { return (Either_1.isRight(e) ? onSuccess(e.right, true) : onFailure(e.left, true)); }, function (e) { return (Either_1.isRight(e) ? onSuccess(e.right, false) : onFailure(e.left, false)); })(fea);
}; };
var _a = pipeable_1.pipeable(exports.datumEither), alt = _a.alt, ap = _a.ap, apFirst = _a.apFirst, apSecond = _a.apSecond, bimap = _a.bimap, chain = _a.chain, chainFirst = _a.chainFirst, flatten = _a.flatten, map = _a.map, mapLeft = _a.mapLeft;
exports.alt = alt;
exports.ap = ap;
exports.apFirst = apFirst;
exports.apSecond = apSecond;
exports.bimap = bimap;
exports.chain = chain;
exports.chainFirst = chainFirst;
exports.flatten = flatten;
exports.map = map;
exports.mapLeft = mapLeft;
//# sourceMappingURL=DatumEither.js.map